import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const ReviewPassword = ({ acct, onDone }) => {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const id = acct.email.split("@")[0];
  const save = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    try { await api.post("/admin/demo-data/review-password", { email: acct.email, password: pw }); toast.success(`Review password set for ${acct.email}`); setPw(""); setPw2(""); onDone(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={save} className="rounded-xl border border-white/10 p-4" data-testid={`review-password-form-${id}`}>
      <p className="text-sm text-amber-100">{acct.email}</p>
      <p data-testid={`review-password-status-${id}`} className="mt-1 text-xs text-zinc-500">{!acct.exists ? "Account doesn't exist here" : acct.review_password_set_at ? `Private password set ${fmtDate(acct.review_password_set_at)}` : "No private password yet — disabled on the live site"}</p>
      <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
        <input type="password" autoComplete="new-password" minLength={12} required data-testid={`review-password-input-${id}`} className="lux-input" placeholder="New password (12+ characters)" value={pw} onChange={(e) => setPw(e.target.value)} />
        <input type="password" autoComplete="new-password" minLength={12} required data-testid={`review-password-confirm-${id}`} className="lux-input" placeholder="Repeat password" value={pw2} onChange={(e) => setPw2(e.target.value)} />
        <button type="submit" disabled={!acct.exists} data-testid={`review-password-save-${id}`} className="btn-gold">Set password</button>
      </div>
    </form>
  );
};

export const DemoDataAdmin = () => {
  const [s, setS] = useState(null);
  const [keep, setKeep] = useState(true);
  const [phrase, setPhrase] = useState("");
  const [result, setResult] = useState(null);
  const load = useCallback(() => api.get("/admin/demo-data", { params: { keep_review_accounts: keep } }).then(({ data }) => setS(data)).catch((e) => toast.error(errMsg(e))), [keep]);
  useEffect(() => { load(); }, [load]);
  const remove = async () => {
    try { const { data } = await api.post("/admin/demo-data/remove", { confirm: phrase, keep_review_accounts: keep }); setResult(data.removed); setPhrase(""); toast.success("Demo data removed"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!s) return null;
  return (
    <div className="space-y-6" data-testid="demo-data-admin">
      <div className="lux-card space-y-1 p-5 sm:p-6">
        <p className="lux-eyebrow">Environment</p>
        <p data-testid="demo-environment" className="font-serif text-2xl capitalize text-amber-50">{s.environment}</p>
        <p className="text-xs text-zinc-500">On the live site only the master admin can sign in as admin, demo accounts work only after you set a private password, and no demo data is seeded.</p>
      </div>
      <div className="lux-card space-y-3 p-5 sm:p-6">
        <p className="lux-eyebrow">App-review demo accounts</p>
        <p className="text-xs text-zinc-500">Set a private password for the Apple / Google review accounts. It is stored hashed and never shown again — write it down before saving.</p>
        {s.review_accounts.map((a) => <ReviewPassword key={a.email} acct={a} onDone={load} />)}
      </div>
      <div className="lux-card space-y-4 p-5 sm:p-6">
        <p className="lux-eyebrow">Remove demo data</p>
        <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-5" data-testid="demo-counts">
          {Object.entries(s.counts).map(([k, v]) => <div key={k} className="rounded-lg bg-white/[0.03] p-3"><p className="text-[11px] capitalize text-zinc-500">{k.replace(/_/g, " ")}</p><p data-testid={`demo-count-${k}`} className="font-serif text-xl text-amber-50">{v}</p></div>)}
        </div>
        <label className="flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" data-testid="demo-keep-review-checkbox" checked={keep} onChange={(e) => setKeep(e.target.checked)} /> Keep the app-review accounts (customer@, driver1@)</label>
        <AlertDialog>
          <AlertDialogTrigger asChild><button data-testid="demo-remove-button" className="btn-danger">Remove demo data</button></AlertDialogTrigger>
          <AlertDialogContent data-testid="demo-remove-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Remove all demo data?</AlertDialogTitle>
              <AlertDialogDescription>This permanently deletes only seeded demo records: demo accounts{keep ? " (except the review accounts)" : ""}, their demo trips, ratings, comments and complaints, sample pickups, sample specials/promos and sample ads. Real users and real trips are never touched. Type {s.confirm_phrase} to confirm.</AlertDialogDescription>
            </AlertDialogHeader>
            <input data-testid="demo-confirm-input" className="lux-input" value={phrase} onChange={(e) => setPhrase(e.target.value)} placeholder={s.confirm_phrase} />
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="demo-remove-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="demo-remove-confirm" disabled={phrase.trim() !== s.confirm_phrase} onClick={remove}>Delete demo data</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        {result && <p data-testid="demo-remove-result" className="text-sm text-emerald-300">Removed: {Object.entries(result).filter(([, v]) => v).map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`).join(", ") || "nothing"}</p>}
      </div>
    </div>
  );
};
